<script setup lang="ts">
import type { ProgressItem } from './KUIUnifiedBadgeProgressRow.vue';

import KUIUnifiedBadgePremium from './KUIUnifiedBadgePremium.vue';
import KUIUnifiedBadgeProgressRow from './KUIUnifiedBadgeProgressRow.vue';

interface Props {
  imageUrl?: string | null;
  partnerBgColor: string;
  imageAlt?: string;
  badgeSize?: string;
  progress?: ProgressItem[];
  iconPath?: string;
  hideProgressRow?: boolean;
  premiumBadgeUrl?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  imageUrl: null,
  imageAlt: '',
  badgeSize: 'size-64',
  progress: () => [],
  iconPath: '/images/kui/unified/anyway-shield-badge.webp',
  hideProgressRow: false,
  premiumBadgeUrl: null,
});

// Callers lay out the hero differently for the 3D badge, so they need to know when it
// drops to the static render.
const emit = defineEmits<{
  premiumError: [];
}>();

const isAllCompleted = computed(() =>
  props.progress.length > 0 && props.progress.every(p => p.completed),
);

// Callers only pass premiumBadgeUrl for an earned badge — an in-progress badge
// keeps the grayscale static render, which a full-colour 3D model can't convey.
// Falls back to the static image if the model fails to load.
const premiumFailed = ref(false);
const premiumSrc = computed(() =>
  (premiumFailed.value ? null : props.premiumBadgeUrl),
);
</script>

<template>
  <div class="flex flex-col items-center justify-center">
    <KUIUnifiedBadgePremium
      v-if="premiumSrc"
      :src="premiumSrc"
      :alt="imageAlt"
      class="mb-2"
      @error="premiumFailed = true; emit('premiumError')"
    />
    <NuxtImg
      v-else-if="imageUrl"
      :src="imageUrl"
      :alt="imageAlt"
      class="aspect-square mb-2 select-none pointer-events-none transition-[filter,opacity] duration-700 ease-out"
      :class="[badgeSize, { 'grayscale opacity-95 mix-blend-luminosity-screen': !isAllCompleted }]"
    />
    <Transition name="progress-fade">
      <div v-if="progress.length && !hideProgressRow" class="z-20 flex w-full items-center justify-center">
        <KUIUnifiedBadgeProgressRow
          :progress="progress"
          :icon-path="iconPath"
          :partner-bg-color="partnerBgColor"
        />
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.progress-fade-leave-active {
  transition: opacity 0.4s ease, transform 0.4s ease;
}

.progress-fade-leave-to {
  opacity: 0;
  transform: scale(0.8);
}
</style>
